/**
 * Hook for handling menu scope navigation
 * Decouples menu scope switching logic from navigation and store management
 * Provides unified interface for scope selection across the application
 *
 * @module hooks/menuScope/useMenuScopeNavigation
 */
import { useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { NavigationType } from '@experimental-components/TopNavigationBar/types';
import { MENU_SCOPE, MENU_ROUTES } from 'utils/menuMaker/constants';
import { useMenuScopeManagement } from 'hooks/menuScope';
import useNavigation from 'hooks/navigation/useNavigation';
import useUserStore from 'hooks/useUserStore';
import useLicenseState from 'hooks/useLicenseState';
import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import { useUser } from 'containers/Users';
import { isMenuPath } from 'components/TopNavigationBarStores/utils';

/**
 * Options for menu scope navigation hook
 *
 * @interface UseMenuScopeNavigationOptions
 */
interface UseMenuScopeNavigationOptions {
  /**
   * Function to navigate to store draft menu
   *
   * @type {() => void}
   * @memberof UseMenuScopeNavigationOptions
   */
  onNavigateToStoreDraftMenu?: () => void;

  /**
   * Whether to automatically redirect to resume page after scope selection
   * Used by MenuScope component
   *
   * @type {boolean}
   * @memberof UseMenuScopeNavigationOptions
   */
  autoRedirectToResume?: boolean;
}

/**
 * Return type for useMenuScopeNavigation hook
 *
 * @interface MenuScopeNavigationReturn
 */
interface MenuScopeNavigationReturn {
  /**
   * Currently selected menu scope
   *
   * @type {MENU_SCOPE | null}
   * @memberof MenuScopeNavigationReturn
   */
  selectedScope: MENU_SCOPE | null;

  /**
   * Toggles between ORGANIZATION and STORE scopes
   *
   * @type {() => MENU_SCOPE}
   * @memberof MenuScopeNavigationReturn
   */
  toggleMenuScope: () => MENU_SCOPE;

  /**
   * Handles scope navigation based on current route
   * Changes scope and navigates accordingly
   *
   * @type {() => void}
   * @memberof MenuScopeNavigationReturn
   */
  handleScopeNavigation: () => void;

  /**
   * Sets a specific scope without navigation
   *
   * @type {(scope: MENU_SCOPE) => void}
   * @memberof MenuScopeNavigationReturn
   */
  setScope: (scope: MENU_SCOPE) => void;

  /**
   * Handles complete scope selection with navigation state management
   * Updates both menu scope and navigation type with proper store management
   *
   * @type {(scope: MENU_SCOPE) => void}
   * @memberof MenuScopeNavigationReturn
   */
  handleScopeSelection: (scope: MENU_SCOPE) => void;
}

/**
 * Hook for handling menu scope navigation
 * Provides functions to toggle scope, handle navigation, and manage store state
 * Unifies scope management logic across TopNavigationBarStores and MenuScope
 *
 * @param {UseMenuScopeNavigationOptions} options - Configuration options
 * @returns {MenuScopeNavigationReturn} Object with scope navigation functions
 */
export const useMenuScopeNavigation = (options: UseMenuScopeNavigationOptions = {}): MenuScopeNavigationReturn => {
  const { onNavigateToStoreDraftMenu, autoRedirectToResume = false } = options;
  const location = useLocation();
  const history = useHistory();
  const { logEvent } = useAmplitude();

  // Get menu scope state and functions
  const { selectedScope, setMenuScope, clearMenuScope } = useMenuScopeManagement();

  // Navigation and store management hooks
  const { currentNavigation: typeNavigation, setNavigation } = useNavigation();
  const { setModeDraft } = useSidebarLayout();
  const { licenseStatus } = useLicenseState();
  const { remove: resetUser } = useUser();
  const {
    storeState: { storeDraft, selected: storeSelected, stores = [] },
    setStore,
    storeBackup,
    setStoreBackup,
  } = useUserStore();

  /**
   * Toggles between ORGANIZATION and STORE scopes
   * If no scope is selected or current scope is STORE, sets to ORGANIZATION
   * Otherwise, sets to STORE
   *
   * @returns {MENU_SCOPE} The new scope after toggle
   */
  const toggleMenuScope = useCallback((): MENU_SCOPE => {
    const newScope = !selectedScope || selectedScope === MENU_SCOPE.STORE ? MENU_SCOPE.ORGANIZATION : MENU_SCOPE.STORE;

    if (newScope === MENU_SCOPE.STORE) {
      clearMenuScope();
    } else {
      setMenuScope(newScope);
    }

    return newScope;
  }, [selectedScope, setMenuScope, clearMenuScope]);

  /**
   * Handles navigation based on current scope and route
   * Changes scope and navigates according to current path
   */
  const handleScopeNavigation = useCallback((): void => {
    // Handle navigation based on current route
    if (isMenuPath(location.pathname)) {
      // Change scope
      toggleMenuScope();

      onNavigateToStoreDraftMenu?.();
    }
  }, [location.pathname, toggleMenuScope, onNavigateToStoreDraftMenu]);

  /**
   * Sets a specific scope directly without navigation
   *
   * @param {MENU_SCOPE} scope - Scope to set
   */
  const setScope = useCallback(
    (scope: MENU_SCOPE): void => {
      if (scope === MENU_SCOPE.STORE) {
        clearMenuScope();
      } else {
        setMenuScope(scope);
      }
    },
    [setMenuScope, clearMenuScope],
  );

  /**
   * Handles complete scope selection with navigation state management
   * This function unifies the logic used in MenuScope component
   * Updates both menu scope and navigation type with proper store management
   *
   * @param {MENU_SCOPE} scope - Selected scope (ORGANIZATION or STORE)
   */
  const handleScopeSelection = useCallback(
    (scope: MENU_SCOPE): void => {
      setMenuScope(scope);

      if (scope === MENU_SCOPE.ORGANIZATION) {
        // Configure state for organization navigation
        if (typeNavigation !== NavigationType.STORE_DRAFT) {
          // Backup current store before switching
          setStoreBackup(storeSelected);
          setModeDraft(true);
          setNavigation(NavigationType.STORE_DRAFT);
          setStore(storeDraft);

          // Reset user to sync brands with organization context
          resetUser();

          // Track store view event
          logEvent('@event.$store.viewStore', '', {
            store: storeDraft?.uuid,
            isDraft: storeDraft?.isDraft,
            license: licenseStatus,
          });
        }
      } else {
        // Configure state for store navigation
        // eslint-disable-next-line no-lonely-if
        if (typeNavigation !== NavigationType.STORE) {
          const changeStore = storeBackup ?? stores[0];
          setModeDraft(false);
          setNavigation(NavigationType.STORE);
          setStore(changeStore);

          // Reset user to sync brands with store context
          resetUser();

          // Track store view event
          logEvent('@event.$store.viewStore', '', {
            store: changeStore?.uuid,
            isDraft: changeStore?.isDraft,
            license: licenseStatus,
          });
        }
      }

      // Navigate to resume page if auto redirect is enabled
      if (autoRedirectToResume) {
        history.replace(MENU_ROUTES.RESUME);
      }
    },
    [
      typeNavigation,
      setStoreBackup,
      storeSelected,
      setModeDraft,
      setNavigation,
      setStore,
      storeDraft,
      storeBackup,
      stores,
      setMenuScope,
      resetUser,
      logEvent,
      licenseStatus,
      autoRedirectToResume,
      history,
    ],
  );

  return {
    selectedScope,
    toggleMenuScope,
    handleScopeNavigation,
    setScope,
    handleScopeSelection,
  };
};

export default useMenuScopeNavigation;
